import './video-footer.scss'

import { ControlsSideRight } from './ControlsStyled'
import { useCallback, useState, useEffect } from 'react'
import { useZoomContext } from '../../../../context/ZoomContext'
import { getDeviceType } from '../../util/platform'
import styled from 'styled-components'
import { ShareContent } from './ShareContent'
import { useContentSharing } from '../../../../context/ContentSharingContext'
import { StopShareContent } from './StopShareContent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Tip } from '../../../../components/Tip'
import { useTranslation } from 'react-i18next'
import { useSessionParams } from '../../../../hooks/useSessionParams'
import { useUserCourses } from '../../../../pages/meeting-view/hooks/useCourses'

const ControlsRight = () => {
    const { sidebarMode, setSidebarMode, unReadMessages, setUnReadMessages } = useZoomContext()
    const { t } = useTranslation('meeting')
    const type: 'mobile' | 'desktop' = getDeviceType()
    const { isContentShared, isContentSharer } = useContentSharing()
    const [activeMode, setActiveMode] = useState<'content' | 'mentor'>('content')
    const { room } = useSessionParams()
    const { currentCourse } = useUserCourses()
    const enableSharedContent = !!currentCourse?.courseFlags.enableSharedContent
    const hasUnreadMentorMessages = unReadMessages && unReadMessages.mentor && !isContentSharer

    // Sync the toggle state with sidebarMode when it changes externally
    useEffect(() => {
        if (sidebarMode === 'content') {
            setActiveMode('content')
        } else if (sidebarMode === 'mentor') {
            setActiveMode('mentor')
            setUnReadMessages(prev => {
                if (!prev) return false
                const newState = { ...prev }
                delete newState.mentor
                // If no unread messages left, set to false
                return Object.keys(newState).length > 0 ? newState : false
            })
        }
    }, [sidebarMode])

    const handleModeToggle = () => {
        const newMode = activeMode === 'content' ? 'mentor' : 'content'
        setActiveMode(newMode)
        setSidebarMode(newMode)

        // If opening the mentor sidebar, clear unread status for mentor
        if (newMode === 'mentor') {
            setUnReadMessages(prev => {
                if (!prev) return false
                const newState = { ...prev }
                delete newState.mentor
                // If no unread messages left, set to false
                return Object.keys(newState).length > 0 ? newState : false
            })
        }
    }

    const shareGroupRender = useCallback(() => {
        if (type === 'mobile') return null
        if (!isContentShared) return <ShareContent />
        if (isContentShared)
            return (
                <ShareGroup>
                    <ToggleContainer>
                        <ToggleSwitch
                            active={activeMode}
                            onClick={handleModeToggle}
                            data-tooltip-id="mode-switch-tooltip"
                        >
                            <IconWrapper active={activeMode === 'mentor'}>
                                <FontAwesomeIcon icon={['far', 'message-smile']} />
                                {/* Unread message indicator */}
                                {hasUnreadMentorMessages && (
                                    <UnreadIndicator>
                                        <FontAwesomeIcon icon={['fas', 'circle']} />
                                    </UnreadIndicator>
                                )}
                            </IconWrapper>
                            <IconWrapper active={activeMode === 'content'}>
                                <FontAwesomeIcon icon={['far', 'folder-open']} />
                            </IconWrapper>
                            <ToggleKnob position={activeMode} />
                        </ToggleSwitch>
                        <Tip
                            id="mode-switch-tooltip"
                            content={activeMode === 'content' ? t('controls.viewMentor') : t('controls.viewContent')}
                        />
                    </ToggleContainer>
                    {isContentSharer && <StopShareContent />}
                </ShareGroup>
            )
        return null
    }, [type, isContentShared, isContentSharer, activeMode, t, hasUnreadMentorMessages])

    if (!enableSharedContent || room === 'general') return null

    return <ControlsSideRight showSidebar={sidebarMode}>{shareGroupRender()}</ControlsSideRight>
}

export default ControlsRight

const ShareGroup = styled.div`
    background: ${props => props.theme.color.aiGradient2};
    padding: 4px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    gap: 10px;
`

const ToggleContainer = styled.div`
    position: relative;
`

const ToggleSwitch = styled.div<{ active: 'content' | 'mentor' }>`
    position: relative;
    width: 110px;
    height: 40px;
    background-color: ${props => props.theme.color.midnightBlack};
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding: 0;
    cursor: pointer;
    overflow: hidden;
`

const IconWrapper = styled.div<{ active: boolean }>`
    z-index: 2;
    color: white;
    width: 55px;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: ${props => (props.active ? 1 : 0.7)};
    position: relative;

    svg {
        font-size: 16px;
    }
`

const UnreadIndicator = styled.div`
    position: absolute;
    top: -10px;
    right: 10px;

    color: #ff647c;
    svg {
        font-size: 10px !important;
    }
`

const ToggleKnob = styled.div<{ position: 'content' | 'mentor' }>`
    position: absolute;
    top: 3px;
    left: ${props => (props.position === 'mentor' ? '3px' : '55px')};
    width: 52px;
    height: 34px;
    background-color: ${props => props.theme.color.sidebar.gray2};
    border-radius: 10px;
    transition: left 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    z-index: 1;
`
